import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Tag, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { useGooglePlaces, type Prediction } from "@/hooks/useGooglePlaces";

const BookingWidget = () => {
  const navigate = useNavigate();
  const [routeType, setRouteType] = useState<"one-way" | "round-trip">("one-way");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromPlaceId, setFromPlaceId] = useState("");
  const [toPlaceId, setToPlaceId] = useState("");
  const [passengers, setPassengers] = useState("2");
  const [promoCode, setPromoCode] = useState("");

  const fromPlaces = useGooglePlaces();
  const toPlaces = useGooglePlaces();

  const isAirportRoute = fromPlaceId.startsWith("db-airport-") || toPlaceId.startsWith("db-airport-");
  const maxPassengers = isAirportRoute ? 50 : 10;

  useEffect(() => {
    if (parseInt(passengers) > maxPassengers) {
      setPassengers(String(maxPassengers));
    }
  }, [maxPassengers]);

  const handleFromSelect = (prediction: Prediction) => {
    setFrom(prediction.description);
    setFromPlaceId(prediction.place_id);
    fromPlaces.setPredictions([]);
  };

  const handleToSelect = (prediction: Prediction) => {
    setTo(prediction.description);
    setToPlaceId(prediction.place_id);
    toPlaces.setPredictions([]);
  };

  const handleSearch = () => {
    navigate("/vehicles", {
      state: {
        from, to, fromPlaceId, toPlaceId, routeType,
        passengers: parseInt(passengers),
        promoCode,
      },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.3 }}
      className="w-full max-w-4xl mx-auto"
    >
      <div className="glass-dark rounded-2xl p-6 md:p-8 border border-white/10 shadow-2xl">
        <h2 className="font-display text-xl md:text-2xl font-bold text-white mb-6">
          Book Your <span className="text-primary">Transfer</span>
        </h2>

        {/* Route Type Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setRouteType("one-way")}
            className={`px-5 py-2 rounded-full text-sm font-semibold font-sans transition-all ${
              routeType === "one-way"
                ? "bg-primary text-primary-foreground"
                : "bg-white/10 text-white/70 hover:bg-white/20"
            }`}
          >
            One Way
          </button>
          <button
            onClick={() => setRouteType("round-trip")}
            className={`px-5 py-2 rounded-full text-sm font-semibold font-sans transition-all ${
              routeType === "round-trip"
                ? "bg-primary text-primary-foreground"
                : "bg-white/10 text-white/70 hover:bg-white/20"
            }`}
          >
            Round Trip
          </button>
        </div>

        {/* Form Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <PlaceAutocomplete
            placeholder="Pick-up: Airport or Hotel"
            value={from}
            onChange={setFrom}
            onSelect={handleFromSelect}
            predictions={fromPlaces.predictions}
            loading={fromPlaces.loading}
            onSearch={fromPlaces.search}
            iconColor="text-primary"
          />
          <PlaceAutocomplete
            placeholder="Drop-off: Hotel or Address"
            value={to}
            onChange={setTo}
            onSelect={handleToSelect}
            predictions={toPlaces.predictions}
            loading={toPlaces.loading}
            onSearch={toPlaces.search}
            iconColor="text-orange-light"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Passengers */}
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary z-10" />
            <Select value={passengers} onValueChange={setPassengers}>
              <SelectTrigger aria-label="Number of passengers" className="pl-11 h-12 bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Passengers" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxPassengers }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1} {i === 0 ? "Passenger" : "Passengers"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Promo Code */}
          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
            <input
              aria-label="Promo code"
              placeholder="Promo Code (optional)"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex h-12 w-full rounded-md border border-white/10 bg-white/5 px-3 pl-11 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0"
            />
          </div>

          {/* Search Button */}
          <Button
            onClick={handleSearch}
            size="lg"
            className="h-12 text-base font-sans font-bold gap-2 rounded-xl shadow-lg shadow-primary/25"
          >
            <Search className="w-5 h-5" />
            Search Vehicles
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default BookingWidget;
